import React, { useEffect, useState } from 'react';

import styled from 'styled-components';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import CtaButton from 'components/CtaButton/CtaButton';

import { CtaButtonI } from 'interfaces/content-stack/contentStack.types';
import { FetchPriority } from 'interfaces/html';

import { autoOptimizeImage } from 'utils/functions';
import { getModularBlockLink } from 'utils/modularBlockUtils';
import { sanitizeAndUpdatePreviewPaths } from 'utils/textUtils';

const MastheadSection = styled.section`
  view-timeline-name: --mastheadTimeline;
  background-color: ${(props) => props.theme.colors.black};
  color: ${(props) => props.theme.colors.white};
  position: relative;
  overflow: hidden;
  font-family: ${(props) => props.theme.fonts.default};
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  min-height: 27rem;
  align-items: center;
  display: flex;

  @media screen and (min-width: 1440px) {
    width: 100%;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    padding-top: 10rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding-top: 4rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    padding-top: 7rem;
  }
`;

const MastheadGrid = styled.div`
  grid-column-gap: 5rem;
  grid-row-gap: 3rem;
  grid-template-rows: auto;
  grid-template-columns: 1fr 1fr;
  grid-auto-columns: 1fr;
  display: grid;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-row-gap: 1rem;
    grid-template-columns: 1fr;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    grid-row-gap: 2rem;
  }
`;

const HeadingsContainer = styled.div`
  grid-row-gap: 1.25rem;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1rem;
  display: flex;
`;

const ButtonsContainer = styled.div`
  grid-area: span 1 / span 2 / span 1 / span 2;
  opacity: 1;
  grid-column-gap: 1.5rem;
  grid-row-gap: 1rem;
  flex-direction: row;
  align-items: center;
  display: flex;
  animation: fadeIn 2s;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-column: span 1 / span 1;
    flex-wrap: wrap;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    flex-direction: column;
    grid-row-gap: 1.5rem;
  }
`;

const BackgroundFilter = styled.div`
  will-change: opacity;
  opacity: 0.17827;
  z-index: 2;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0%;
  bottom: 0%;
  left: 0%;
  right: 0%;
  animation: fadeOut 3s;

  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0.17827;
    }
  }

  @media screen and (min-width: 1920px) {
    opacity: 0;
    position: absolute;

    @keyframes fadeOut {
      0% {
        opacity: 1;
      }
      100% {
        opacity: 0;
      }
    }
  }
`;

const ScrollingBlur = styled.div<{ $blur?: number; $opacity?: number }>`
  will-change: backdrop-filter;
  opacity: ${({ $opacity }) => $opacity};
  z-index: 2;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0%;
  bottom: 0%;
  left: 0%;
  right: 0%;

  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
`;

const HeaderOverlay = styled.div`
  z-index: 3;
  width: 100%;
  mix-blend-mode: multiply;
  background-image: linear-gradient(to right, #1d191b 40%, rgba(27, 27, 27, 0));
  position: absolute;
  top: 0%;
  bottom: 0%;
  left: 0%;
  right: 0%;

  @media screen and (min-width: 1920px) {
    background-image: linear-gradient(to right, rgba(29, 25, 27, 0.95) 36%, rgba(27, 27, 27, 0) 92%);
  }

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    background-image: linear-gradient(to top, rgba(29, 25, 27, 0.9) 44%, rgba(27, 27, 27, 0.3));
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    background-image: linear-gradient(to top, rgba(29, 25, 27, 0.9) 12%, rgba(27, 27, 27, 0.5));
  }
`;

const HeaderMask = styled.div`
  overflow: hidden;
`;

const MastheadHeader = styled.h1`
  transform: translate3d(0px, 0%, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg);
  transform-style: preserve-3d;
  font-size: 4rem;
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
  text-transform: uppercase;
  margin-top: 0;
  margin-bottom: 0;
  font-weight: 500;
  line-height: 1.4;
  animation: slideIn 1s;

  @keyframes slideIn {
    0% {
      transform: translate3d(0px, 100%, 0px);
    }
    100% {
      transform: translate3d(0px, 0%, 0px);
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const MastheadSubheader = styled.div`
  opacity: 1;
  margin-top: 0;
  margin-bottom: 0;
  font-weight: 500;
  animation: fadeIn 2s;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  sup {
    top: -0.5em;
    vertical-align: baseline;
    font-size: 75%;
    line-height: 0;
    position: relative;
  }
`;

const BackgroundImage = styled.img.withConfig({ shouldForwardProp: () => true })`
  transform: translate3d(0px, 0%, 0px) scale3d(1, 1, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg);
  transform-style: preserve-3d;
  animation: zoomOut 2s;

  @keyframes zoomOut {
    0% {
      transform: scale3d(1.2, 1.2, 1.2);
    }
    100% {
      transform: scale3d(1, 1, 1);
    }
  }

  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0%;
  bottom: 0%;
  left: 0%;
  right: 0%;
`;

interface StaticMastheadProps {
  masthead: {
    header: string;
    subheader: string;
    image: { altImageText: string; imageAsset: { url: string } };
    buttonCta: CtaButtonI[];
  };
}

const StaticMasthead: React.FC<StaticMastheadProps> = ({ masthead }) => {
  const [scrollBlur, setScrollBlur] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const newBlur = Math.min(20, window.scrollY / 50);
      setScrollBlur(newBlur);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <MastheadSection>
      <ContentContainer innerPaddingOverride='5rem'>
        <MastheadGrid>
          <HeadingsContainer>
            <HeaderMask>
              <MastheadHeader data-elastic-name={'page_title'}>{masthead.header}</MastheadHeader>
            </HeaderMask>
            <MastheadSubheader
              dangerouslySetInnerHTML={{ __html: sanitizeAndUpdatePreviewPaths(masthead.subheader) }}
            />
          </HeadingsContainer>
          <ButtonsContainer>
            {masthead.buttonCta?.map((buttonCta, index) => (
              <CtaButton
                key={index}
                textColor='white'
                to={getModularBlockLink(buttonCta.buttonLink)}
                buttonType={buttonCta.buttonType}
                text={buttonCta.buttonText}
                openInNewWindow={!!buttonCta.buttonLink[0]?.openInNewWindow}
                banner
                dataLayerInfo={masthead.header}
              />
            ))}
          </ButtonsContainer>
        </MastheadGrid>
      </ContentContainer>
      <BackgroundImage
        src={masthead?.image?.imageAsset?.url && autoOptimizeImage(masthead.image.imageAsset.url, true, '')}
        fetchPriority={FetchPriority.HIGH}
      />
      <BackgroundFilter />
      {/* Using inline styles for `ScrollingBlur` due to high-frequency updates, optimizing performance. */}
      <ScrollingBlur style={{ backdropFilter: `blur(${scrollBlur}px)`, opacity: scrollBlur ? 1 : 0 }} />
      <HeaderOverlay />
    </MastheadSection>
  );
};

export default StaticMasthead;
